import {
  Alert,
  AlertType,
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Grid,
  Text,
  TextSize,
  TextWeight,
  makeStyles
} from '@focaldata/cin-ui-components'
import Divider from '@mui/material/Divider'
import { useEffect } from 'react'
import LaunchErrorDetails from '../../../../controls/LaunchErrorDetails'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import {
  formatThousandsWithCommas,
  getDaysSuffix
} from '../../../../utils/HelperFunctions'
import { MarketLaunchAudienceSummary } from './MarketLaunchAudienceSummary'
import { useMarketLaunch } from './useMarketLaunch'

const useStyles = makeStyles()({
  // The audience summary is unbounded (one row per criterion), so it scrolls
  // within the dialog instead of pushing the cost rows and the Launch button
  // off-screen on short viewports.
  audienceSummary: { maxHeight: 280, overflowY: 'auto', marginTop: 8 }
})

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Grid container direction="row" justifyContent="space-between">
    <Text size={TextSize.ms}>{label}</Text>
    <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
      {value}
    </Text>
  </Grid>
)

const MarketLaunchDialogBody = ({
  projectId,
  surveyId,
  marketName,
  isFirstLaunch,
  onClose,
  onLaunched
}: {
  projectId: string
  surveyId: string
  marketName: string
  isFirstLaunch: boolean
  onClose: () => void
  onLaunched: () => void | Promise<unknown>
}) => {
  const { classes } = useStyles()
  const waveId = useOptionalWaveId()
  const {
    summary,
    showCostBreakdown,
    canLaunch,
    placeOrder,
    isPlacingOrder,
    loadingData,
    isOrderPlaced,
    hasCostChanged,
    failedToLaunch,
    launchErrorDetails
  } = useMarketLaunch(projectId, surveyId, onLaunched)

  // Close once the order is placed; the project refetch (onLaunched) flips the
  // market row to its live/paused action.
  useEffect(() => {
    if (isOrderPlaced) onClose()
  }, [isOrderPlaced, onClose])

  const { costPerComplete, completes, totalCost } = summary
  // Always render the same rows (with "…" placeholders while loading) so the
  // dialog keeps a fixed size instead of growing once the data arrives.
  const placeholder = '…'

  return (
    <Dialog
      open
      fullWidth
      title={`Launch ${marketName}`}
      maxWidth={DialogMaxWidth.Md}
      variant={DialogVariant.DoubleButton}
      primaryButtonText="Launch"
      primaryButtonClick={placeOrder}
      primaryButtonDisabled={!canLaunch || isPlacingOrder}
      primaryButtonLoading={isPlacingOrder}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
      onClose={onClose}
      onClickOutside={onClose}
    >
      {isFirstLaunch && (
        <Alert severity={AlertType.WARNING} style={{ marginBottom: 16 }}>
          {waveId
            ? "This is the wave's first launch — its questionnaire locks once fieldwork starts. Markets added to the wave afterwards receive the full questionnaire and their visibility can no longer be adjusted, so make sure all markets are locked in first."
            : "This is the project's first launch — the shared questionnaire locks once fieldwork starts. Markets added afterwards receive the full questionnaire and their visibility can no longer be adjusted, so make sure all markets are locked in first."}
        </Alert>
      )}
      {hasCostChanged && (
        <Alert severity={AlertType.ERROR} style={{ marginBottom: 16 }}>
          The cost changed since this was opened. Re-open to review the new cost
          before launching.
        </Alert>
      )}
      {failedToLaunch && (
        <Alert severity={AlertType.ERROR} style={{ marginBottom: 16 }}>
          Something went wrong launching this market. Please try again.
          {launchErrorDetails && (
            <LaunchErrorDetails details={launchErrorDetails} />
          )}
        </Alert>
      )}
      <Grid container direction="column" spacing={1}>
        <SummaryRow
          label="Go live date"
          value={loadingData ? placeholder : summary.goLiveDate}
        />
        <SummaryRow
          label="Estimated time to complete"
          value={
            loadingData
              ? placeholder
              : `${summary.estimatedDeliveryDays} ${getDaysSuffix(
                  summary.estimatedDeliveryDays
                )}`
          }
        />
        <SummaryRow
          label="Completion date"
          value={loadingData ? placeholder : summary.completionDate}
        />
        {showCostBreakdown && (
          <SummaryRow
            label="Cost per complete"
            value={
              costPerComplete ? `£${costPerComplete.toFixed(2)}` : placeholder
            }
          />
        )}
        <SummaryRow
          label="Number of completes"
          value={completes ? formatThousandsWithCommas(completes) : placeholder}
        />
        {showCostBreakdown && (
          <SummaryRow
            label="Total cost"
            value={
              totalCost
                ? `£${formatThousandsWithCommas(totalCost)} (GBP)`
                : placeholder
            }
          />
        )}
      </Grid>
      <Divider style={{ margin: '16px 0' }} />
      <Text size={TextSize.m} weight={TextWeight.SemiBold}>
        Quotas and sample quality
      </Text>
      {/* The summary is read-only, so nothing inside the scroll region is
          focusable — it needs to be focusable itself for keyboard users to
          reach criteria below the fold. */}
      <div
        className={classes.audienceSummary}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable region needs a tab stop to be operable by keyboard (WCAG 2.1.1)
        tabIndex={0}
        role="region"
        aria-label="Quotas and sample quality"
      >
        <MarketLaunchAudienceSummary surveyId={surveyId} />
      </div>
    </Dialog>
  )
}

/**
 * Launch-confirmation dialog for a single market. Mirrors the single-survey
 * Confirmation page's cost/feasibility summary + order placement, but the data
 * is fetched per-market by surveyId (see useMarketLaunch) so it needs no
 * route/navigation.
 *
 * The wrapper/body split is deliberate: returning null before rendering the
 * body is what keeps the market's cost, questionnaire and audience queries from
 * running while the dialog is closed. Hoisting the body's hooks up here — or
 * rendering it behind a hidden prop — would fire them for every market row on
 * the Confirm page.
 */
export const MarketLaunchDialog = ({
  open,
  projectId,
  surveyId,
  marketName,
  isFirstLaunch,
  onClose,
  onLaunched
}: {
  open: boolean
  projectId: string
  surveyId: string
  marketName: string
  isFirstLaunch: boolean
  onClose: () => void
  onLaunched: () => void | Promise<unknown>
}) => {
  if (!open) return null

  return (
    <MarketLaunchDialogBody
      projectId={projectId}
      surveyId={surveyId}
      marketName={marketName}
      isFirstLaunch={isFirstLaunch}
      onClose={onClose}
      onLaunched={onLaunched}
    />
  )
}
